import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { images } from '../config/esanzaContent';
import { company, mission, vision, status, team, TeamMember } from '../config/company';
import { positioning } from '../config/product';
import { contentText, useWebsiteContent } from '../hooks/useWebsiteContent';

const initialsFor = (name: string) => (
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
);

const TeamPhoto: React.FC<{ image: string; name: string }> = ({ image, name }) => {
  const [failed, setFailed] = useState(!image);

  if (failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,.38),transparent_32%),linear-gradient(135deg,#102016,#06150e)]">
        <div className="flex h-24 w-24 items-center justify-center rounded-full border border-[#d4af37]/35 bg-white/10 text-3xl font-black text-[#f3d978] shadow-xl">
          {initialsFor(name)}
        </div>
      </div>
    );
  }

  return (
    <img
      src={image}
      alt={name}
      onError={() => setFailed(true)}
      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
    />
  );
};

const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const { page, settings } = useWebsiteContent('about');
  const company_name = contentText(page, 'company', company.legalName);
  const cmsMission = contentText(page, 'mission', '');
  const cmsVision = contentText(page, 'vision', '');
  const cmsStatus = contentText(page, 'status', '');
  const headline =
    page?.title ||
    'Building intelligent infrastructure for modern agriculture.';
  const intro =
    page?.summary ||
    'eSanza Limited is an agricultural technology company building a connected operating system for farms, cooperatives, agribusinesses, buyers, extension teams and agricultural institutions.';
  const introSecondary =
    'Founded in Rwanda, eSanza combines farm management, AI-assisted agronomy, traceability, supply-chain operations and B2B market infrastructure.';
  const principles = (t('site.about.principles', { returnObjects: true }) as string[]) || [];
  const storyTitle = t('site.about.story_title', 'Built by operators.');
  const storyText = t(
    'site.about.story_text',
    'The team brings together software engineering, field operations, agronomic research and institutional partnership building. The work ahead is field validation: registering farms and crops, training teams, tracking harvest lots, and preparing buyer-ready records.'
  );

  return (
    <>
      <SEO
        title="About | eSanza"
        description="eSanza builds the intelligent operating system for agriculture: a connected platform for farms, cooperatives, agribusinesses, buyers and agricultural institutions."
        imagePath={images.tree}
        url="https://esanza.com/about"
      />
      <PageShell>
        <section className="relative overflow-hidden px-5 pb-20 pt-32 lg:px-8">
          <img src="/images/esanza/banner.png" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#06150e_0%,rgba(6,21,14,.88)_50%,rgba(6,21,14,.55)_100%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">{company_name}</p>
              <h1 className="mt-5 max-w-4xl text-5xl font-black leading-[1] tracking-tight text-white md:text-6xl">
                {headline}
              </h1>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.08] p-8 backdrop-blur">
              <p className="text-lg leading-8 text-white/76">{intro}</p>
              <p className="mt-4 text-base leading-7 text-white/60">{introSecondary}</p>
            </div>
          </div>
        </section>

        <section className="bg-[#f7f9f4] px-5 py-20 text-[#102016] lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-5 md:grid-cols-3">
              <div className="rounded-lg border border-[#dbe5d2] bg-white p-7 shadow-sm">
                <h2 className="text-xl font-black">Mission</h2>
                <p className="mt-3 text-sm leading-6 text-[#596959]">{cmsMission || mission}</p>
              </div>
              <div className="rounded-lg border border-[#dbe5d2] bg-white p-7 shadow-sm">
                <h2 className="text-xl font-black">Vision</h2>
                <p className="mt-3 text-sm leading-6 text-[#596959]">{cmsVision || vision}</p>
              </div>
              <div className="rounded-lg border border-[#dbe5d2] bg-white p-7 shadow-sm">
                <h2 className="text-xl font-black">Company status</h2>
                <p className="mt-3 text-sm leading-6 text-[#596959]">
                  {cmsStatus || String(settings.company_status || company.registration)}
                </p>
                <p className="mt-3 text-sm leading-6 text-[#596959]">{company.marketStatement}</p>
                <p className="mt-4 inline-flex rounded-full border border-[#14532d]/25 bg-[#14532d]/[0.06] px-3 py-1 text-xs font-bold text-[#14532d]">
                  {status.platform} {status.field}
                </p>
              </div>
            </div>

            <div className="mt-20 grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#987a1f]">
                  {t('site.about.story_label', 'Our story')}
                </p>
                <h2 className="mt-4 text-4xl font-black tracking-tight md:text-5xl">{storyTitle}</h2>
                <p className="mt-5 text-base leading-7 text-[#596959]">{storyText}</p>
                <p className="mt-4 text-sm leading-6 text-[#596959]">{positioning.origin}</p>
              </div>
              <img
                src={images.traceability}
                alt="eSanza traceability workflow"
                loading="lazy"
                className="h-full max-h-[520px] w-full rounded-lg border border-[#dbe5d2] object-cover shadow-xl"
              />
            </div>

            {principles.length > 0 && (
              <div className="mt-20 grid gap-4 md:grid-cols-4">
                {principles.map((principle) => (
                  <div
                    key={principle}
                    className="rounded-lg border border-[#dbe5d2] bg-[#102016] p-6 text-sm font-extrabold text-white"
                  >
                    {principle}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="bg-[#06150e] px-5 py-24 text-white lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">
                {t('site.about.team_label', 'Team')}
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight md:text-5xl">
                {t('site.about.team_title', 'Four founders. One operating system.')}
              </h2>
              <p className="mt-5 text-base leading-7 text-white/64">
                {t(
                  'site.about.team_text',
                  'A leadership team spanning company strategy, operations, platform execution and agricultural AI research.'
                )}
              </p>
            </div>

            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {team.map((member: TeamMember) => (
                <article key={member.name} className="group flex flex-col items-center text-center">
                  <div className="relative h-32 w-32 overflow-hidden rounded-full border-2 border-[#d4af37]/30 bg-[#102016] shadow-xl shadow-[#d4af37]/10 transition duration-500 group-hover:border-[#d4af37] group-hover:shadow-[#d4af37]/25">
                    <TeamPhoto image={member.image} name={member.name} />
                  </div>
                  <h3 className="mt-5 text-lg font-black leading-tight">{member.name}</h3>
                  <p className="mt-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#d4af37]">
                    {member.role}
                  </p>
                  <p className="mt-3 max-w-[260px] text-sm leading-6 text-white/56">{member.focus}</p>
                  {member.linkedIn && (
                    <a
                      href={member.linkedIn}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${member.name} LinkedIn profile`}
                      className="mt-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/12 bg-white/[0.06] text-xs font-black text-white/60 transition hover:border-[#d4af37]/60 hover:text-[#d4af37]"
                    >
                      in
                    </a>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      </PageShell>
    </>
  );
};

export default AboutPage;
